import {
  IconHomeCog,
  IconPlugConnected,
  IconHomeSearch,
  IconSettings,
  IconUserBolt,
  IconUserCheck,
} from '@tabler/icons-react'

import { Role } from '@/modules/adminUsers/roles'
import {
  PROJECT_TAB_FILTER_KEY,
  ProjectTabs,
} from '@/modules/projects/constants'

import { IntegrationType } from 'types/api.graphql'
import { QuerySupport, RouteViewOptions } from 'types/navigation'

const allRoles = [
  Role.BuilderOwner,
  Role.BuilderManager,
  Role.SuperAdmin,
  Role.BuilderProjectManager,
  Role.BpSuperAdmin,
  Role.BuilderAccounts,
  Role.BuilderViewOnly,
]

export const SETTINGS_CONFIG = [
  {
    id: 'companySettings' as const,
    title: 'Company settings',
    path: '/admin/settings/company',
    icon: IconHomeCog,
    querySupport: QuerySupport.BUILDER_ONLY,
    requiredRoles: [Role.BuilderOwner, Role.BuilderManager, Role.SuperAdmin],
  },
  {
    id: 'integrations' as const,
    title: 'Integrations',
    path: '/admin/settings/integrations',
    icon: IconPlugConnected,
    querySupport: QuerySupport.BUILDER_ONLY,
    requiredRoles: [Role.BuilderOwner, Role.BuilderManager, Role.SuperAdmin],
    actions: {
      list: () => '/admin/settings/integrations',
      procore: () => '/admin/settings/integrations/procore',
      procoreSetup: () => '/admin/settings/integrations/procore/set-up',
      ironLogic: () => '/admin/settings/integrations/ironLogic',
      ironLogicSetup: () => '/admin/settings/integrations/ironLogic/set-up',
    },
  },
  {
    id: 'adminUsers' as const,
    title: 'Admin users',
    path: '/admin/settings/admin-users',
    icon: IconUserCheck,
    requiredRoles: [Role.BuilderOwner, Role.BuilderManager, Role.SuperAdmin],
    actions: {
      list: () => '/admin/settings/admin-users',
      invite: () => '/admin/settings/admin-users/invite',
    } as const,
    disabledForLimitedPermissions: true,
  },
  {
    id: 'projects' as const,
    title: 'Projects',
    path: '/admin/projects',
    icon: IconHomeSearch,
    actions: {
      list: () => '/admin/projects',
      new: () => '/admin/projects/new',
      edit: (projectId: string) =>
        `/admin/projects/${projectId}/edit?projectId=${projectId}`,
      view: (
        projectId: string,
        options?: RouteViewOptions<keyof typeof ProjectTabs>
      ) => {
        return `/admin/projects/${projectId}${
          options?.tab
            ? `?${PROJECT_TAB_FILTER_KEY}=${options.tab}&projectId=${projectId}`
            : `?projectId=${projectId}`
        }`
      },
    } as const,
    querySupport: QuerySupport.BUILDER_ONLY,
    requiredRoles: allRoles,
  },
  {
    id: 'roles' as const,
    title: 'Roles',
    path: '/admin/settings/roles',
    icon: IconUserBolt,
    requiredRoles: [Role.BuilderOwner, Role.BuilderManager, Role.SuperAdmin],
    actions: {
      list: () => '/admin/settings/roles',
    } as const,
  },
  {
    id: 'settings' as const,
    title: 'Settings',
    path: '/admin/settings',
    icon: IconSettings,
    help: `General BuildPass settings and settings for each module.`,
    requiredRoles: [Role.BuilderOwner, Role.BuilderManager, Role.SuperAdmin],
    actions: {
      general: () => '/admin/settings?tab=general',
      swms: () => '/admin/settings?tab=swms',
      timesheets: () => '/admin/settings?tab=timesheets',
      checklists: () => '/admin/settings?tab=checklists',
      integrations: (integrationType: IntegrationType) =>
        '/admin/settings?tab=integrations&integration=' +
        integrationType.toLowerCase(),
      actions: () => '/admin/settings?tab=actions',
      inductions: () => '/admin/settings?tab=inductions',
      subcontractors: () => '/admin/settings?tab=subcontractors',
      signon: () => '/admin/settings?tab=signon',
      aiTools: () => '/admin/settings?tab=aitools',
      defects: () => '/admin/settings?tab=defects',
    },
  },
]
